import { hasOwnProperty } from "../../infra3d";
import { LAYERTYPE, FEATURELOADINGSTRATEGY, Layer, LAYERPROPERTYTYPE } from "./internal";
import { transformGeoJSON } from "./utils";
/**
 * Extension of the Layer-Class for Memory-Layers.
 * @class MemoryLayer
 * @author Joel Burkhard
 *
 */
export class MemoryLayer extends Layer {
    constructor(scene, opt_options) {
        super(scene, opt_options);
        this.srid = -1;
        /**
         * Feature-Index for fast accessing by featureid.
         */
        this._dataidx = {};
        this._id = 0;
        const options = opt_options || {};
        this.strategy = options.strategy || FEATURELOADINGSTRATEGY.ALL;
        this.type = LAYERTYPE.MEMORYLAYER;
        this.data = options.data || { type: "FeatureCollection", features: [] };
        this.srid = options.srid || -1;
        this._parseData();
    }
    _parseData() {
        if (this.data.type == "FeatureCollection") {
            for (const fidx in this.data.features) {
                if (hasOwnProperty(this.data.features, fidx)) {
                    const feature = this.data.features[fidx];
                    if (!hasOwnProperty(feature, "id")) {
                        if (hasOwnProperty(feature.properties, "id")) {
                            feature.id = feature.properties.id;
                        }
                        else if (hasOwnProperty(feature.properties, "ID")) {
                            feature.id = feature.properties.ID;
                        }
                        else {
                            feature.id = this._generateId();
                        }
                    }
                    const id = feature.id;
                    this._dataidx[id] = feature;
                    if (typeof id == "number") {
                        if (id > this._id)
                            this._id = id;
                    }
                    const date_props = this.propertydef.filter((propertydef) => propertydef.datatype == LAYERPROPERTYTYPE.DATE);
                    for (const date_prop of date_props) {
                        const prop = feature.properties[date_prop.key];
                        if (prop instanceof Date)
                            feature.properties[date_prop.key] = prop.toISOString();
                    }
                }
            }
        }
    }
    /**
     * Memory-specific implementation of the feature-load-function. This function can still be overwritten
     * by a given function 'loadFeatures' in the options.
     * @param extent
     * @param srid
     */
    _loadFeatures_internal(extent, srid) {
        let geojson = null;
        if (this.srid != -1) {
            geojson = transformGeoJSON(this.data, this.srid, srid);
        }
        else {
            geojson = JSON.parse(JSON.stringify(this.data));
        }
        this.addFeatures(geojson, true);
    }
    /**
     * Saves all the changes done by the LayerEditor with the api. Resets the LayerEditor if the posts are
     * successful.
     */
    _saveFeatures_internal(inserts, updates, deletions, cbs, cbf) {
        //Inserts
        for (const insf in inserts.features) {
            if (!inserts.features[insf].id)
                inserts.features[insf].id = this._generateId();
            let featuretoinsert = JSON.parse(JSON.stringify(inserts.features[insf]));
            const insert_srid = parseInt(inserts.crs.properties.name.split(":").splice(-1)[0]);
            if (this.srid != -1 && this.srid != insert_srid) {
                featuretoinsert = transformGeoJSON(featuretoinsert, insert_srid, this.srid);
            }
            this.data.features.push(featuretoinsert);
            this._dataidx[inserts.features[insf].id] = featuretoinsert;
        }
        //Updates
        for (const uf in updates.features) {
            let featuretoupdate = JSON.parse(JSON.stringify(updates.features[uf]));
            const update_srid = parseInt(inserts.crs.properties.name.split(":").splice(-1)[0]);
            if (this.srid != -1 && this.srid != update_srid) {
                featuretoupdate = transformGeoJSON(featuretoupdate, update_srid, this.srid);
            }
            this._dataidx[updates.features[uf].id].geometry = featuretoupdate.geometry;
            this._dataidx[updates.features[uf].id].properties = featuretoupdate.properties;
        }
        //Deletions
        for (const i in deletions.features) {
            for (let jf = 0; jf < this.data.features.length; jf++) {
                if (deletions.features[i].id == this.data.features[jf].id) {
                    this.data.features.splice(jf, 1);
                    break;
                }
            }
            delete this._dataidx[deletions.features[i].id];
        }
        setTimeout(() => {
            cbs(inserts, updates, deletions);
        }, 500);
    }
    _generateId() {
        return (this._id += 1);
    }
    _exportFeatures_internal(cbs, cbf, options) {
        const countonly = typeof options.countonly === "undefined" ? false : options.countonly;
        const sorters = typeof options.sorters === "undefined" ? false : options.sorters;
        const start = options.start || 0;
        const limit = options.limit || 10000;
        if (countonly) {
            cbs(this.data.features.length);
        }
        else {
            if (sorters) {
                if (sorters.length > 0) {
                    Layer.localSort(this.data, sorters[0]);
                }
            }
            let data = {
                type: "FeatureCollection",
                features: this.data.features.slice(start, start + limit),
            };
            if (this.srid != -1 && options.srid) {
                if (this.srid != options.srid)
                    data = transformGeoJSON(data, this.srid, options.srid);
            }
            cbs(JSON.parse(JSON.stringify(data)));
        }
    }
    /**
     * Sets the following options from data (first feature in collection):
     * geometrytype
     * coordsdim
     * propertydef
     */
    static optionsFromData(geojson, opt_options) {
        const override_options = opt_options || {};
        let options = {};
        let validgeojson = false;
        if (hasOwnProperty(geojson, "type")) {
            if (geojson.type == "FeatureCollection" && geojson.features.length > 0)
                validgeojson = true;
        }
        if (validgeojson) {
            // Geometrytype
            options.geometrytype = geojson.features[0].geometry.type;
            const geometrytype = options.geometrytype;
            // Coordinate Dimension
            switch (geometrytype) {
                case "Point":
                    options.coordsdim = geojson.features[0].geometry.coordinates.length;
                    break;
                case "LineString":
                case "MultiPoint":
                    options.coordsdim = geojson.features[0].geometry.coordinates[0].length;
                    break;
                case "Polygon":
                case "MultiLineString":
                    options.coordsdim = geojson.features[0].geometry.coordinates[0][0].length;
                    break;
                case "MultiPolygon":
                    options.coordsdim = geojson.features[0].geometry.coordinates[0][0][0].length;
                    break;
            }
            // Propertydef
            // Get the Propertie-Definition from the first feature in the featurecollection
            const propertydefs = [];
            const props = geojson.features[0].properties;
            for (const key in props) {
                if (hasOwnProperty(props, key)) {
                    const jstype = typeof props[key];
                    const propertydef = {
                        key: key,
                    };
                    switch (jstype) {
                        case "string":
                            propertydef["datatype"] = LAYERPROPERTYTYPE.STRING;
                            propertydef["defaultvalue"] = "";
                            break;
                        case "number":
                            propertydef["datatype"] = LAYERPROPERTYTYPE.NUMBER;
                            propertydef["defaultvalue"] = 0;
                            break;
                        case "boolean":
                            propertydef["datatype"] = LAYERPROPERTYTYPE.BOOLEAN;
                            propertydef["defaultvalue"] = true;
                            break;
                        case "object":
                            if (props[key] instanceof Date) {
                                propertydef["datatype"] = LAYERPROPERTYTYPE.DATE;
                                propertydef["defaultvalue"] = new Date().toISOString();
                                propertydef["dateformat"] = "c";
                            }
                            else {
                                propertydef["datatype"] = LAYERPROPERTYTYPE.STRING;
                                propertydef["defaultvalue"] = "";
                            }
                    }
                    propertydefs.push(propertydef);
                }
            }
            if (propertydefs.length > 0)
                options.propertydef = propertydefs;
        }
        options = { ...options, ...override_options };
        return options;
    }
}
//# sourceMappingURL=memorylayer.js.map